import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
// utils
import localStorageAvailable from '../utils/minimal/localStorageAvailable';
import { getI18nLocalStorageValue, getI18nLocalStorageKey } from './utils';
import { setAxiosLanguage } from '../utils/minimal/axios';
//
import { defaultLang } from './config-lang';
//
import { resolveInitialLanguage, isSupportedLang } from './resolveLanguage';
import { getBootstrapData } from '../utils/market';

// Language bundles are loaded on demand so a visitor downloads only the language they
// actually read (#1100) — statically importing both put ~190KB of translations in the
// entry chunk for everyone. A literal map rather than a templated import() keeps this to
// exactly two chunks and makes an unknown code impossible to request.
const langLoaders = {
  en: () => import('./langs/en'),
  he: () => import('./langs/he'),
};

// ----------------------------------------------------------------------

const currencies = {
  ILS: {
    name: 'ש״ח',
    symbol: '₪',
    format: '{{value}} ₪',
  },
  USD: {
    name: 'USD',
    symbol: '$',
    format: '${{value}}',
  },
  EUR: {
    name: 'EUR',
    symbol: '€',
    format: '€{{value}}',
  },
  GBP: {
    name: 'GBP',
    symbol: '£',
    format: '£{{value}}',
  },
}
let currency = currencies[getBootstrapData().defaultCurrency] || currencies.ILS;

const storageAvailable = localStorageAvailable();

const { lang: lng, fromUrl } = resolveInitialLanguage({
  search: window.location.search,
  storedLang: storageAvailable ? getI18nLocalStorageValue() : null,
  defaultLang: defaultLang.value,
});

// Save the language from URL to localStorage for future visits
if (fromUrl && storageAvailable) {
  localStorage.setItem(getI18nLocalStorageKey(), lng);
}

// Set initial language header
setAxiosLanguage(lng);

/**
 *  some marketplaces have their own terminology that should be used in the language files
 * @param {*} langFile 
 * @param {string} lang - language code ('en' or 'he')
 * @returns 
 */
const insertMarketplaceTerminology = (langFile, lang) => {
  // fetch terminology from the marketplace config in market.js
  const bootstrapData = getBootstrapData();
  const terminology = bootstrapData?.i18n?.[lang]?.terminology;
  if (terminology) {
    // for each key and value in the terminology object, override the language file
    Object.keys(terminology).forEach((key) => {
      const keyParts = key.split('.');
      let currentObject = langFile;
      keyParts.forEach((part, index) => {
        if (index === keyParts.length - 1) {
          currentObject[part] = terminology[key];
        } else {
          if (!currentObject[part]) {
            currentObject[part] = {};
          }
          currentObject = currentObject[part];
        }
      });
    });
  }
  langFile.currency = currency;
  return langFile;
}

/**
 * Fetch a language bundle and register it. Safe to call repeatedly — i18next already holds
 * the bundle after the first call, so we skip both the import and the terminology pass.
 * @param {string} lang - 'en' | 'he'
 */
export const loadLanguageBundle = async (lang) => {
  if (!isSupportedLang(lang) || i18n.hasResourceBundle(lang, 'translations')) return;

  const bundle = (await langLoaders[lang]()).default;
  i18n.addResourceBundle(lang, 'translations', insertMarketplaceTerminology(bundle, lang), true, true);
};

/**
 * Load every bundle needed to render `lang` with today's fallback behaviour intact.
 *
 * `fallbackLng` is the marketplace default, and en/he are NOT at parity — 12 keys currently
 * exist in he but not in en (`npm run check-lang-parity`). Those keys render as Hebrew for an
 * English user today via the fallback; if we loaded only the active bundle they would render
 * as raw key strings instead. So when the active language is not the default, the default
 * bundle is fetched alongside it.
 *
 * Net effect: visitors on the default language (the majority) stop downloading the other
 * language entirely; visitors who switch away pay what they already pay today. Once parity is
 * clean this can drop to a single bundle for everyone.
 */
const loadBundlesFor = async (lang) => {
  await loadLanguageBundle(lang);

  if (lang !== defaultLang.value) {
    await loadLanguageBundle(defaultLang.value);
  }
};

/**
 * Boot i18next with only the language(s) actually needed. Awaited in main.jsx before the first
 * render, so no component ever renders against an empty bundle (i.e. no untranslated flash).
 */
export const initI18n = async () => {
  if (i18n.isInitialized) {
    await loadBundlesFor(i18n.language);
    return i18n;
  }

  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      // Bundles are registered after init by loadBundlesFor rather than inlined here.
      resources: {},
      lng,
      // Unchanged from before lazy loading: falling back to the marketplace default is what
      // keeps the 12 he-only keys readable for English users. loadBundlesFor guarantees the
      // default bundle is present whenever it could actually be needed.
      fallbackLng: defaultLang.value,
      debug: false,
      ns: ['translations'],
      defaultNS: 'translations',
      interpolation: {
        escapeValue: false,
      },
      detection: {
        lookupLocalStorage: getI18nLocalStorageKey(),
      },
    });

  await loadBundlesFor(lng);

  return i18n;
};

// Guards against out-of-order switches. Bundles are fetched over the network now, so two
// quick clicks can resolve in the wrong order and the slower, older request would otherwise
// win — snapping the UI back to the language the user just left.
let languageRequestId = 0;

/**
 * Switch language, fetching the target bundle first. Every caller must go through this
 * rather than i18n.changeLanguage directly, or the UI switches to a language that has no
 * strings loaded yet and renders raw keys.
 *
 * Callers should await this and only commit side effects (text direction, localStorage) when
 * it resolves true, so direction and strings always flip together.
 *
 * @param {string} lang - 'en' | 'he'
 * @returns {Promise<boolean>} true if this request was still the current one and applied
 */
export const changeLanguage = async (lang) => {
  languageRequestId += 1;
  const requestId = languageRequestId;

  await loadBundlesFor(lang);

  // A newer switch started (or completed) while we were fetching — drop this one.
  if (requestId !== languageRequestId) return false;

  await i18n.changeLanguage(lang);
  return true;
};

// Update language header when language changes
i18n.on('languageChanged', (lng) => {
  setAxiosLanguage(lng);
});

/**
 * Update the i18n currency configuration dynamically based on seller's account
 * @param {string} currencyCode - Currency code from account (e.g., 'GBP', 'USD', 'ILS', 'EUR')
 */
export const updateI18nCurrency = (currencyCode) => {
    if (!currencyCode) return;

    const upperCurrency = currencyCode.toUpperCase();
    const newCurrency = currencies[upperCurrency];

    // If currency not supported, keep current currency (marketplace default)
    if (!newCurrency) return;

    currency = newCurrency;

    // Update the currency in resource bundles directly (passed by reference, so mutation works)
    const heBundle = i18n.getResourceBundle('he', 'translations');
    const enBundle = i18n.getResourceBundle('en', 'translations');

    if (heBundle) {
        heBundle.currency = newCurrency;
    }

    if (enBundle) {
        enBundle.currency = newCurrency;
    }
};

export default i18n;
